/* Penoblo Geschenkverpackung im Warenkorb.

   Die Auswahl haengt als Positions-Eigenschaft am Artikel; das
   Verpackungsprodukt liegt zusaetzlich als eigene Position im Warenkorb.
   Beides muss in Deckung bleiben — auch wenn mehrere Artikel dieselbe Farbe
   waehlen oder eine Menge geaendert wird.

   Verfahren nach jeder Aenderung:
     1. Eigenschaften der betroffenen Position setzen oder loeschen
        (POST /cart/change.js).
     2. Aus dem zurueckgegebenen Warenkorb die SOLL-Menge je Verpackungsvariante
        neu berechnen (siehe sollMengen) und in einem Zug setzen
        (POST /cart/update.js). Das ist selbstheilend: auch ein zuvor
        verrutschter Stand wird dabei richtiggestellt.
     3. Die Warenkorb-Abschnitte neu zeichnen lassen.

   Absolute Mengen statt Zaehlen: /cart/update.js setzt die Menge, es addiert
   nicht. Ein doppelt gesendeter Klick kann die Verpackung also nicht
   verdoppeln. */

/** SOLL-Menge je Verpackungsvariante aus dem Warenkorb ableiten.

    Reine Funktion ohne DOM und ohne Netz — genau deshalb pruefbar
    (tests/pnb-geschenkverpackung.test.js).

    @param {Array}  items      cart.items aus /cart.js
    @param {Array}  variantIds alle bekannten Verpackungs-Varianten-IDs
    @returns {Object} Varianten-ID -> gewuenschte Menge (immer alle IDs, damit
             abgewaehlte Farben ausdruecklich auf 0 gesetzt werden)
*/
function sollMengen(items, variantIds) {
  const soll = {};
  variantIds.forEach((id) => {
    soll[String(id)] = 0;
  });

  (items || []).forEach((item) => {
    const props = item.properties || {};
    const gewaehlt = props._pnb_giftwrap_variant;
    if (!gewaehlt) return;
    const key = String(gewaehlt);
    // Nur bekannte Verpackungsvarianten zaehlen. Eine Eigenschaft, die auf
    // eine geloeschte Variante zeigt, darf nichts in den Warenkorb legen.
    if (!(key in soll)) return;
    // Der verpackte Artikel selbst darf nicht mitzaehlen, wenn er zufaellig
    // eine Verpackung ist.
    if (variantIds.map(String).includes(String(item.variant_id))) return;
    soll[key] += item.quantity || 0;
  });

  return soll;
}

/** Eigenschaften einer Zeile fuer das Schreiben zusammenfuehren.

    cart/change.js ERSETZT die Eigenschaften einer Position, es ergaenzt sie
    nicht. Wer nur die Verpackung schickt, loescht damit alles andere — im
    Live-Shop verschwanden so Gravurtext, Schriftart und Schreibhand, sobald
    ein Kunde die Geschenkverpackung anhakte; in der Bestellung fehlte die
    Gravur (Befund 22.09.2026). Das Alt-Theme hat aus demselben Grund vor
    jedem Schreiben die vorhandenen Eigenschaften geholt und ergaenzt
    (custom.js, addPropertyLineItem).

    Leere Werte bleiben als leere Zeichenkette drin: so loescht Shopify die
    Eigenschaft, und wo sie stehen bliebe, zeigt sie der Warenkorb ohnehin
    nicht (property.last != blank).

    @param {?Object} vorhanden Eigenschaften der Zeile laut Warenkorb
    @param {string}  label     sichtbarer Name der Verpackungs-Eigenschaft
    @param {?string} variantId gewaehlte Verpackungsvariante (null = keine)
    @param {?string} titel     Farbname zur Variante
    @returns {Object} vollstaendiger Eigenschaftensatz fuer change.js
*/
function eigenschaftenZusammenfuehren(vorhanden, label, variantId, titel) {
  const props = Object.assign({}, vorhanden || {});
  props[label] = variantId ? titel || '' : '';
  props._pnb_giftwrap_variant = variantId ? String(variantId) : '';
  return props;
}

/** SOLL-Menge der Gebuehren-Position fuer die kostenpflichtige Gravur.

    Wie beim Verpackungsprodukt: eine Gravur unter der Gratis-Schwelle wird
    als eigenes Produkt berechnet, und dessen Menge muss der Zahl der
    gravierten Artikel folgen. Das Alt-Theme hat das nach jeder Aenderung
    nachgerechnet (custom.js, updateEngraving); im neuen Theme fehlte es, und
    der Kunde konnte die Gebuehr aus dem Warenkorb loeschen oder die Menge
    des gravierten Stifts erhoehen, ohne dass die Gebuehr mitzog.

    Gezaehlt wird jede Position mit Gravurtext, deren Stueckpreis unter der
    Schwelle liegt. Ab der Schwelle ist die Gravur kostenlos, dort zaehlt
    nichts — auch dann nicht, wenn die Produktseite (die den niedrigsten
    Variantenpreis kennt) die Gebuehr zunaechst mitgelegt hat.

    @param {Array}  items   cart.items
    @param {Object} gebuehr { variante: Varianten-ID, freiAbCent: Schwelle }
    @returns {Object} Varianten-ID -> Menge; leer, wenn nichts konfiguriert
*/
function sollGebuehr(items, gebuehr) {
  const variante = gebuehr && gebuehr.variante ? String(gebuehr.variante) : '';
  if (!variante) return {};
  const freiAb = gebuehr.freiAbCent > 0 ? Number(gebuehr.freiAbCent) : 0;
  let menge = 0;
  (items || []).forEach((item) => {
    if (String(item.variant_id) === variante) return;
    const props = item.properties || {};
    const text = props.Gravurtext;
    if (text == null || String(text).trim() === '') return;
    if (freiAb && Number(item.price) >= freiAb) return;
    menge += item.quantity || 0;
  });
  const soll = {};
  soll[variante] = menge;
  return soll;
}

/** Die Gebuehren-Konfiguration, die layout/theme.liquid auf jeder Seite
    ausgibt (Theme-Einstellungen „Penoblo Gravur“). */
function gebuehrKonfig() {
  const g = typeof window !== 'undefined' ? window.pnbGravurGebuehr : null;
  if (!g || !g.variante) return null;
  return { variante: String(g.variante), freiAbCent: Number(g.freiAbCent) || 0 };
}

/** Weicht der IST-Stand im Warenkorb vom SOLL ab?

    Ohne diese Pruefung wuerde jeder Warenkorb-Vorgang eine Schreib-Anfrage
    ausloesen — auch die, die dieser Code selbst angestossen hat, was in eine
    Endlosschleife liefe.

    @param {Object} soll  Varianten-ID -> gewuenschte Menge
    @param {Array}  items cart.items
*/
function brauchtAngleich(soll, items) {
  const ist = {};
  Object.keys(soll).forEach((id) => {
    ist[id] = 0;
  });
  (items || []).forEach((item) => {
    const id = String(item.variant_id);
    if (id in ist) ist[id] += item.quantity || 0;
  });
  return Object.keys(soll).some((id) => soll[id] !== ist[id]);
}

/** Alle Verpackungs-Varianten-IDs.

    Zuerst die Liste, die layout/theme.liquid auf jeder Seite ausgibt — so
    hielt es das Alt-Theme (giftWrapProducts). Sie ist auch dann da, wenn kein
    einziger verpackbarer Artikel mehr im Warenkorb liegt: genau dann muss
    eine verwaiste Verpackung auf 0 gesetzt werden, und aus dem DOM allein
    waere die Liste in diesem Moment leer. Die Felder auf der Seite bleiben
    als Rueckfall. */
function bekannteVarianten(doc) {
  const ids = new Set();
  const global = typeof window !== 'undefined' ? window.pnbGiftwrapVariants : null;
  if (Array.isArray(global)) global.map(String).filter(Boolean).forEach((id) => ids.add(id));
  (doc || document).querySelectorAll('pnb-geschenkverpackung[data-variant-ids]').forEach((el) => {
    (el.dataset.variantIds || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((id) => ids.add(id));
  });
  return Array.from(ids);
}

/** Soll ein Angleich geschrieben werden — und unter welcher Kennung?

    Der Shop erfuellt nicht jedes Ziel. Ist von einer Verpackungsfarbe nur ein
    Stueck lieferbar, bleibt die Menge bei 1, obwohl zwei verlangt sind. Ohne
    Gedaechtnis versucht der Abgleich das bei jedem Warenkorb-Ereignis erneut —
    und weil frueher ein window.location.reload() dazwischen lag, das den
    Wiedereintritts-Schutz zuruecksetzte, drehte sich das endlos: cart.js,
    update.js, neu laden, von vorn. Shopify riegelte mit HTTP 429 ab, und im
    Warenkorb stand "Konnte nicht uebernommen werden". Genau so gemeldet.

    Ein Ziel, das schon einmal nicht aufging, wird deshalb kein zweites Mal
    versucht. Aendert der Kunde etwas, ist das Ziel ein anderes, und der
    Abgleich laeuft wieder an.

    @param {Object} soll           Varianten-ID -> gewuenschte Menge
    @param {Array}  items          cart.items
    @param {?string} letzterVersuch Kennung des letzten Schreibversuchs
    @returns {{schreiben: boolean, kennung: ?string}} kennung ist das neue
             Gedaechtnis; null bedeutet "nichts offen, Gedaechtnis leeren".
*/
function naechsterVersuch(soll, items, letzterVersuch) {
  if (!brauchtAngleich(soll, items)) return { schreiben: false, kennung: null };
  const kennung = JSON.stringify(soll);
  if (kennung === letzterVersuch) return { schreiben: false, kennung };
  return { schreiben: true, kennung };
}

/** Die Warenkorb-Abschnitte neu zeichnen lassen.

    Frueher wurde bei jedem Fehlschlag die Seite neu geladen. Das war der
    Motor der Endlosschleife oben: unter HTTP 429 schlaegt auch dieser Aufruf
    fehl, und jedes Neuladen brachte den naechsten Versuch mit sich. Jetzt
    bleibt die Seite stehen; ein einmal nicht aktualisierter Warenkorb ist
    unschoen, eine sich selbst neu ladende Seite ist unbenutzbar. */
function abschnitteNeuZeichnen(root) {
  const abschnitte = [];
  const cartItems = document.getElementById('main-cart-items');
  if (cartItems && cartItems.dataset.id) {
    abschnitte.push({ id: 'main-cart-items', section: cartItems.dataset.id, selector: '.js-contents' });
  }
  // Die Summe unter der Liste gehoert dazu — Dawn zeichnet sie nach jeder
  // Mengenaenderung mit (assets/cart.js, getSectionsToRender). Ohne sie
  // stand auf der Warenkorbseite die neue Verpackung in der Liste, die
  // Gesamtsumme darunter aber noch auf dem alten Betrag.
  const footer = document.getElementById('main-cart-footer');
  if (footer && footer.dataset.id) {
    abschnitte.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
  }
  const drawer = document.getElementById('CartDrawer');
  if (drawer) {
    abschnitte.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
  }
  abschnitte.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });

  /* Im Kontext der AKTUELLEN Seite anfragen, nicht an der Wurzel: die
     Abschnitte der Warenkorbseite gehoeren zu deren Vorlage und werden nur
     dort gerendert. Dawn schickt aus demselben Grund sections_url =
     window.location.pathname mit. */
  const basis = (typeof window !== 'undefined' && window.location && window.location.pathname) || root;
  const url = basis + '?sections=' + abschnitte.map((a) => a.section).join(',');
  /* Ohne Accept-Header, wie Dawn: mit "Accept: application/json" antwortet
     Shopify unter /cart mit dem Warenkorb-JSON statt mit den Abschnitten.
     Die Anzeige blieb dann stehen — nach dem Entfernen des gravierten
     Artikels stand die Gravur-Gebuehr weiter (ohne Papierkorb) im
     Warenkorb, obwohl er laengst leer war (Befund 19.10.2026). */
  return fetch(url)
    .then((r) => r.json())
    .then((daten) => {
      if (!daten || !abschnitte.some((a) => daten[a.section])) return false;
      abschnitte.forEach((a) => {
        const ziel = document.getElementById(a.id);
        const html = daten[a.section];
        if (!ziel || !html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const neu = doc.querySelector(a.selector);
        const alt = ziel.querySelector(a.selector) || ziel;
        if (neu) alt.innerHTML = neu.innerHTML;
        leerZustandUebernehmen(doc);
      });
      return true;
    })
    .catch(() => false);
}

/** Leer-Zustand aus den frisch gerenderten Abschnitten uebernehmen.

    Dawn schaltet is-empty an Liste, Summe und Schublade selbst um
    (assets/cart.js, updateQuantity); das Neuzeichnen oben tauscht nur den
    Inhalt. Wird der Warenkorb hier leer (letzte Gebuehr oder Verpackung
    entfernt), zeigte die Seite sonst eine leere Liste statt „Dein Warenkorb
    ist leer“. */
function leerZustandUebernehmen(doc) {
  ['cart-items', 'cart-drawer', 'cart-drawer-items', '#main-cart-footer'].forEach((sel) => {
    const neu = doc.querySelector(sel);
    const alt = neu ? document.querySelector(sel) : null;
    if (alt) alt.classList.toggle('is-empty', neu.classList.contains('is-empty'));
  });
}

/** Die Warenkorbzeile zu einem Schluessel finden — auch dann, wenn Shopify
    ihr inzwischen einen anderen Schluessel gegeben hat.

    Der Schluessel einer Position ist Varianten-ID plus ein Hash ihrer
    Eigenschaften. Sobald die Verpackung als Eigenschaft gesetzt oder
    geloescht wird, aendert er sich. Ein Block, der nach dem Schreiben nicht
    neu gezeichnet wurde (Netzfehler, Teilfehler), traegt den alten — und der
    naechste Klick liefe mit ihm ins Leere (HTTP 404), obwohl der Warenkorb
    laengst stimmt. Das Alt-Theme holte deshalb vor jedem Schreiben den
    Warenkorb und suchte die Zeile frisch (custom.js, addPropertyLineItem).

    Reihenfolge: exakter Schluessel; sonst dieselbe Variante mit der
    erwarteten Verpackungs-Eigenschaft; sonst dieselbe Variante ueberhaupt.

    @param {Array}   items      cart.items
    @param {string}  key        zuletzt bekannter Schluessel
    @param {?string} variantId  erwartete Verpackungsvariante (null = keine)
    @returns {?Object} die Zeile oder null
*/
function zeileFinden(items, key, variantId) {
  const liste = items || [];
  const exakt = liste.find((i) => i.key === key);
  if (exakt) return exakt;
  const variante = String(key || '').split(':')[0];
  if (!variante) return null;
  const erwartet = variantId == null ? '' : String(variantId);
  const passend = liste.find(
    (i) => String(i.variant_id) === variante && String((i.properties || {})._pnb_giftwrap_variant || '') === erwartet
  );
  if (passend) return passend;
  return liste.find((i) => String(i.variant_id) === variante) || null;
}

/* Zwei Stellen schreiben in denselben Warenkorb: der Klick im Block und der
   Abgleich am cartUpdate-Ereignis. Schreiben beide gleichzeitig, gewinnt einer
   und der andere bekommt eine Absage — im Warenkorb erschien daraufhin
   "Konnte nicht uebernommen werden", obwohl am Ende alles richtig dastand.
   Genau so gemeldet: Verpackung da, Menge stimmt, Farbe stimmt, und trotzdem
   die rote Meldung.

   Dieses Merkmal haelt die beiden auseinander. Es gehoert modulweit hierher,
   weil sich beide Schreiber dieselbe Warteschlange teilen muessen. */
let schreibtGerade = false;

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { sollMengen, brauchtAngleich, naechsterVersuch, zeileFinden, eigenschaftenZusammenfuehren, sollGebuehr, abschnitteNeuZeichnen };
}

/* Der Abgleich nach einem Klick (siehe unten). Wird gesetzt, sobald der
   Abonnent eingerichtet ist; im Test ohne Browser bleibt er leer. */
let abgleichNachKlick = null;

class PnbGeschenkverpackung extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-toggle]');
    this.colors = this.querySelector('[data-colors]');
    this.busy = this.querySelector('[data-busy]');
    this.errorEl = this.querySelector('[data-error]');
    this.grundEl = this.querySelector('[data-grund]');
    this.radios = Array.from(this.querySelectorAll('[data-colour]'));

    if (!this.toggle || !this.colors) return;

    /* Dawn haengt an das ganze <cart-items> einen change-Listener und haelt
       JEDES change darin fuer eine Mengenaenderung (assets/cart.js,
       CartItems-Konstruktor). Haekchen und Farbfelder liegen in der Zeile —
       ihr change stieg also zu Dawn auf. Dort wurde "on" bzw. eine
       Varianten-ID als Menge gelesen, dem Feld eine Pruefmeldung angehaengt
       ("… nur in Abstufungen von … hinzufuegen", per reportValidity als
       Browser-Blase gezeigt) und danach nach einem Mengenfeld gegriffen, das
       es nicht gibt: "Cannot read properties of null (reading
       'getAttribute')". Solange das Feld die Pruefmeldung trug, nahm der
       Browser das Warenkorb-Formular nicht an — "Zur Kasse" tat nichts.

       Die eigenen Listener sitzen an den Feldern und laufen vorher; hier
       endet das Ereignis, bevor es Dawn erreicht. */
    this.addEventListener('change', (e) => e.stopPropagation());

    this.toggle.addEventListener('change', () => this.onToggle());
    this.radios.forEach((r) => r.addEventListener('change', () => this.onColour(r)));
  }

  get variantIds() {
    return (this.dataset.variantIds || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  get root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Anhaken heisst: verpackt — wie im Alt-Theme (custom.js: beim Anhaken
     wird das erste Farbfeld gesetzt und gespeichert). Die gemerkte Farbe,
     sonst die erste; die Farbe laesst sich danach wechseln.

     Ein Zwischenstand "angehakt, aber nichts im Warenkorb" wurde hier
     zeitweise eingefuehrt, weil die rote Meldung dem Anhaken zugeschrieben
     wurde. Die Meldung hatte eine andere Ursache (siehe pnb-brand.css,
     [hidden]), der Zwischenstand aber einen echten Preis: ein gesetztes
     Haekchen, hinter dem nichts berechnet wird, und ein Haekchen, das beim
     naechsten Neuzeichnen kommentarlos verschwindet. */
  onToggle() {
    if (this.toggle.checked) {
      this.colors.classList.add('pnb-gw__colors--open');
      const gewaehlt = this.radios.find((r) => r.checked) || this.radios[0];
      if (gewaehlt) {
        gewaehlt.checked = true;
        this.onColour(gewaehlt);
      } else {
        // Keine Farbe lieferbar: dann gibt es nichts zu verpacken.
        this.toggle.checked = false;
        this.colors.classList.remove('pnb-gw__colors--open');
      }
    } else {
      /* Geloescht wird, was gespeichert ist — nicht, was gerade markiert
         ist. Beides faellt auseinander, wenn die gemerkte Farbe nicht mehr
         angeboten wird (Variante geloescht oder ausverkauft): dann ist kein
         Feld markiert, die Eigenschaft haengt aber weiter am Artikel, und
         ohne diesen Blick auf data-gespeichert liesse sie sich nie
         entfernen — das Haekchen "ginge immer wieder an". */
      const warGespeichert = Boolean(this.dataset.gespeichert) || this.radios.some((r) => r.checked);
      this.colors.classList.remove('pnb-gw__colors--open');
      this.radios.forEach((r) => (r.checked = false));
      if (warGespeichert) this.speichern(null, null);
    }
  }

  onColour(radio) {
    if (!radio || !radio.value) return;
    this.toggle.checked = true;
    this.colors.classList.add('pnb-gw__colors--open');
    this.speichern(radio.value, radio.dataset.title || '');
  }

  setBusy(an) {
    if (this.busy) this.busy.hidden = !an;
    this.classList.toggle('pnb-gw--busy', an);
  }

  meldungenAus() {
    if (this.errorEl) this.errorEl.hidden = true;
    if (this.grundEl) this.grundEl.hidden = true;
  }

  /** Die Bloecke, die JETZT fuer diese Position auf der Seite stehen.

      Nach dem Neuzeichnen ist dieser Block abgehaengt; Meldungen an ihm saehe
      niemand. Gesucht wird ueber die Variante im Schluessel — so findet sich
      die Zeile auch, wenn der Schluessel sich beim Schreiben geaendert hat,
      und in Warenkorbseite und Schublade zugleich. */
  aktuelleBloecke() {
    const variante = String(this.dataset.lineKey || '').split(':')[0];
    const alle = Array.from(document.querySelectorAll('pnb-geschenkverpackung[data-line-key]'));
    const passende = alle.filter((el) => String(el.dataset.lineKey || '').split(':')[0] === variante);
    return passende.length ? passende : [this];
  }

  /** Die Meldung im Warenkorb bleibt kurz und freundlich — der Kunde kann mit
      einem HTTP-Status nichts anfangen. Der Grund steht leise darunter und in
      der Entwicklerkonsole: aus "Konnte nicht uebernommen werden" allein
      laesst sich nicht ablesen, WELCHE Anfrage gescheitert ist. */
  zeigeFehler(grund) {
    const text = grund && grund.message ? grund.message : String(grund || 'unbekannt');
    this.aktuelleBloecke().forEach((el) => {
      const fehler = el.querySelector('[data-error]');
      const grundEl = el.querySelector('[data-grund]');
      if (fehler) fehler.hidden = false;
      if (grundEl) {
        grundEl.textContent = text;
        grundEl.hidden = false;
      }
    });
    this.setBusy(false);
    if (typeof console !== 'undefined' && console.error) {
      console.error('[Geschenkverpackung] ' + text);
    }
  }

  /** Auswahl speichern. variantId === null loescht die Auswahl.

      Ablauf wie im Alt-Theme, in dieser Reihenfolge:
        1. Warenkorb frisch holen und die Zeile darin finden — Schluessel und
           Menge aus dem Rendern koennen veraltet sein (Menge in der anderen
           Ansicht geaendert, Schluessel nach frueherem Schreiben gewechselt).
        2. Eigenschaften setzen (cart/change.js) und den NEUEN Schluessel
           uebernehmen.
        3. Verpackungsmengen angleichen (cart/update.js). Scheitert das, wird
           die eben gesetzte Eigenschaft zurueckgenommen: "Verpackung: Rot"
           am Artikel ohne Verpackung im Warenkorb waere eine Bestellung, die
           etwas verspricht, was nicht berechnet wurde.
        4. Neu zeichnen — auch nach einem Fehler, damit Haekchen, Farbe und
           Schluessel den echten Stand zeigen und "noch einmal versuchen"
           eine Chance hat. */
  speichern(variantId, titel) {
    const alterKey = this.dataset.lineKey;
    if (!alterKey) return;
    this.meldungenAus();
    this.setBusy(true);
    schreibtGerade = true;

    const root = this.root;
    const label = this.dataset.propertyLabel || 'Verpackung';
    const json = (r, name) => {
      if (!r.ok) throw new Error(name + ' antwortete mit HTTP ' + r.status);
      return r.json();
    };
    const post = (pfad, body) =>
      fetch(root + pfad, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

    let neuerKey = alterKey;

    // Die Kette wird zurueckgegeben, damit ein Test auf ihr Ende warten kann.
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => json(r, 'cart.js'))
      .then((cart) => {
        // Vor dem Schreiben: die Zeile so, wie sie JETZT liegt.
        // data-gespeichert traegt die Variante, die laut letztem Rendern am
        // Artikel haengt — so wird bei zwei Zeilen derselben Variante (eine
        // verpackt, eine nicht) die richtige getroffen.
        const zeile = zeileFinden(cart.items, alterKey, this.dataset.gespeichert || null);
        if (!zeile) throw new Error('Position liegt nicht mehr im Warenkorb');
        // Alle Eigenschaften der Zeile mitschicken (Gravur!), nicht nur die
        // Verpackung — change.js ersetzt den ganzen Satz.
        const properties = eigenschaftenZusammenfuehren(zeile.properties, label, variantId, titel);
        return post('cart/change.js', { id: zeile.key, quantity: zeile.quantity, properties });
      })
      .then((r) => json(r, 'cart/change.js'))
      .then((cart) => {
        const zeile = zeileFinden(cart.items, alterKey, variantId);
        if (zeile) {
          neuerKey = zeile.key;
          this.dataset.lineKey = zeile.key;
        }
        this.dataset.gespeichert = variantId ? String(variantId) : '';
        return this.mengenAngleichen(cart).catch((grund) => {
          // Eigenschaft zuruecknehmen, dann den Fehler weiterreichen — egal,
          // ob das Zuruecknehmen selbst gelingt. Auch hier den ganzen Satz
          // schicken, sonst naehme die Ruecknahme die Gravur mit.
          const leer = eigenschaftenZusammenfuehren(zeile ? zeile.properties : null, label, null, null);
          return post('cart/change.js', { id: neuerKey, properties: leer }).then(
            () => {
              this.dataset.gespeichert = '';
              throw grund;
            },
            () => {
              throw grund;
            }
          );
        });
      })
      .then(() => this.neuZeichnen())
      .catch((grund) =>
        // Auch im Fehlerfall neu zeichnen: die Anzeige soll den echten Stand
        // zeigen, und die Meldung gehoert an den Block, der DANACH da ist.
        this.neuZeichnen().then(
          () => this.zeigeFehler(grund),
          () => this.zeigeFehler(grund)
        )
      )
      .finally(() => {
        schreibtGerade = false;
        this.setBusy(false);
        // Was Dawn waehrend des Klicks am Warenkorb geaendert hat (Menge,
        // Entfernen), wird jetzt nachgezogen.
        if (typeof abgleichNachKlick === 'function') abgleichNachKlick();
      });
  }

  /** Verpackungsmengen auf das SOLL setzen und pruefen, ob der Shop es
      erfuellt hat. Shopify kappt Mengen am Bestand still und antwortet
      trotzdem mit 200 — Dawn vergleicht deshalb nach jedem Schreiben die
      zurueckgegebene Menge mit der gewuenschten (assets/cart.js,
      updateQuantity). Hier ebenso: eine Verpackung, die es nicht in der
      noetigen Zahl gibt, ist ein Fehler, keine Fussnote. */
  mengenAngleichen(cart) {
    // Die Gebuehren-Position der Gravur gleich mit: derselbe Schreibvorgang,
    // dieselbe Selbstheilung.
    const soll = Object.assign(sollMengen(cart.items, this.variantIds), sollGebuehr(cart.items, gebuehrKonfig()));
    if (!Object.keys(soll).length) return Promise.resolve(cart);
    return fetch(this.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: soll }),
    })
      .then((r) => {
        if (!r.ok) throw new Error('cart/update.js antwortete mit HTTP ' + r.status);
        return r.json();
      })
      .then((neu) => {
        if (brauchtAngleich(soll, neu.items)) {
          throw new Error('Verpackung ist nicht in der noetigen Menge lieferbar');
        }
        return neu;
      });
  }

  /** Warenkorb-Abschnitte neu holen — wie Dawn es nach jeder Mengenaenderung
      tut. Ein stilles Scheitern gibt es nicht mehr: dann stuende ein Block
      mit altem Schluessel und alter Anzeige da, der beim naechsten Klick
      ins Leere greift. */
  neuZeichnen() {
    return abschnitteNeuZeichnen(this.root).then((ok) => {
      if (!ok) throw new Error('Warenkorb-Anzeige konnte nicht aktualisiert werden');
      this.setBusy(false);
    });
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('pnb-geschenkverpackung')) {
  customElements.define('pnb-geschenkverpackung', PnbGeschenkverpackung);
}

/* Menge geaendert oder Artikel entfernt? Dann muss die Verpackungsmenge
   nachziehen — sonst bleiben Verpackungen im Warenkorb, deren Artikel nicht
   mehr da ist, oder es fehlen welche nach einer Mengenerhoehung.
   Dawn meldet solche Vorgaenge ueber cartUpdate (assets/cart.js). */
if (typeof window !== 'undefined' && typeof subscribe === 'function') {
  let laeuft = false;
  /* Ein Ereignis, das waehrend eines laufenden Abgleichs oder Klicks kommt,
     wird nicht verworfen, sondern vorgemerkt. Verworfen hiesse: zwei schnelle
     Klicks auf "+" beim verpackten Artikel, und die Verpackungsmenge bleibt
     eine hinter der Artikelmenge zurueck — bis irgendwann ein naechstes
     Ereignis kommt. */
  let nachholen = false;
  // Gedaechtnis fuer ein Ziel, das der Shop nachweislich nicht erfuellt hat
  // (siehe naechsterVersuch). Gesetzt wird es erst NACH dem Schreiben und
  // nur, wenn der Warenkorb danach noch immer abweicht.
  let letzterVersuch = null;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function abgleichen() {
    if (laeuft || schreibtGerade) {
      nachholen = true;
      return;
    }
    const varianten = bekannteVarianten(document);
    const gebuehr = gebuehrKonfig();
    if (!varianten.length && !gebuehr) return;

    laeuft = true;
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        // Verpackungen und Gravur-Gebuehr in einem Zug angleichen.
        const soll = Object.assign(sollMengen(cart.items, varianten), sollGebuehr(cart.items, gebuehr));
        const { schreiben, kennung } = naechsterVersuch(soll, cart.items, letzterVersuch);
        if (!schreiben) {
          letzterVersuch = kennung;
          return null;
        }
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: soll }),
        })
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
          .then((neu) => {
            letzterVersuch = brauchtAngleich(soll, neu.items) ? kennung : null;
            return abschnitteNeuZeichnen(root);
          });
      })
      .catch(() => {})
      .finally(() => {
        laeuft = false;
        if (nachholen) {
          nachholen = false;
          abgleichen();
        }
      });
  }

  abgleichNachKlick = abgleichen;
  subscribe(PUB_SUB_EVENTS.cartUpdate, abgleichen);

  /* Auf der Warenkorbseite einmal beim Laden: ein Stand, der vor dieser
     Version entstanden ist (Gebuehr geloescht, Menge verrutscht), wird so
     richtiggestellt, bevor der Kunde zur Kasse geht. Auf allen anderen
     Seiten reicht das cartUpdate-Ereignis; ein Aufruf je Seitenaufruf waere
     dort verschwendet. */
  if (typeof document !== 'undefined' && document.getElementById('main-cart-items')) {
    const start = () => abgleichen();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  }
}

/* Die Klasse fuer den Test zugaenglich machen. Das Verhalten beim Anhaken ist
   reine Ablauflogik, keine Darstellung — es laesst sich ohne Browser pruefen. */
if (typeof module !== 'undefined' && module.exports) {
  module.exports.PnbGeschenkverpackung = PnbGeschenkverpackung;
}
